import {
  SetStatusActionStatus,
  SetStatusSnoozePeriod,
} from "@redotech/redo-model/advanced-flow/rules/actions";
import type { SetStatusAction as SetStatusActionType } from "@redotech/redo-model/advanced-flow/rules/schemas";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useMemo } from "react";

const statusOptions: RedoListItem<SetStatusActionStatus>[] = [
  {
    id: SetStatusActionStatus.ARCHIVED,
    type: "text",
    text: "Archived",
    value: SetStatusActionStatus.ARCHIVED,
  },
  {
    id: SetStatusActionStatus.CLOSED,
    type: "text",
    text: "Closed",
    value: SetStatusActionStatus.CLOSED,
  },
  {
    id: SetStatusActionStatus.OPEN,
    type: "text",
    text: "Open",
    value: SetStatusActionStatus.OPEN,
  },
  {
    id: SetStatusActionStatus.SNOOZED,
    type: "text",
    text: "Snoozed",
    value: SetStatusActionStatus.SNOOZED,
  },
  {
    id: SetStatusActionStatus.SPAM,
    type: "text",
    text: "Spam",
    value: SetStatusActionStatus.SPAM,
  },
];

const snoozePeriodOptions: RedoListItem<SetStatusSnoozePeriod>[] = [
  {
    id: SetStatusSnoozePeriod.TODAY,
    type: "text",
    text: "Later the same day",
    value: SetStatusSnoozePeriod.TODAY,
  },
  {
    id: SetStatusSnoozePeriod.TOMORROW,
    type: "text",
    text: "The next day",
    value: SetStatusSnoozePeriod.TOMORROW,
  },
  {
    id: SetStatusSnoozePeriod.WEEKEND,
    type: "text",
    text: "The next Saturday",
    value: SetStatusSnoozePeriod.WEEKEND,
  },
  {
    id: SetStatusSnoozePeriod.NEXT_WEEK,
    type: "text",
    text: "The next Monday",
    value: SetStatusSnoozePeriod.NEXT_WEEK,
  },
];

export const SetStatusAction = memo(function SetStatusAction({
  action,
  onChange,
  editableFields,
}: {
  action: SetStatusActionType;
  onChange: (updated: SetStatusActionType) => void;
  editableFields?: ReadonlySet<string>;
}) {
  const statusEditable =
    editableFields === undefined || editableFields.has("status");
  const snoozePeriodEditable =
    editableFields === undefined || editableFields.has("snoozePeriod");

  const selectedStatus = useMemo(() => {
    return statusOptions.find((opt) => opt.value === action.status);
  }, [action.status]);

  const selectedSnoozePeriod = useMemo(() => {
    return snoozePeriodOptions.find((opt) => opt.value === action.snoozePeriod);
  }, [action.snoozePeriod]);

  return (
    <Flex dir="column" gap="md" w="full">
      <RedoSingleSelectDropdownInput<SetStatusActionStatus>
        disabled={!statusEditable}
        fitToAnchor
        label="Status"
        options={statusOptions}
        optionSelected={({ item }) => {
          if (!statusEditable) {
            return;
          }
          onChange({ ...action, status: item.value });
        }}
        selectedItem={selectedStatus}
      />
      {action.status === SetStatusActionStatus.SNOOZED && (
        <RedoSingleSelectDropdownInput<SetStatusSnoozePeriod>
          disabled={!snoozePeriodEditable}
          fitToAnchor
          label="Until"
          options={snoozePeriodOptions}
          optionSelected={({ item }) => {
            if (!snoozePeriodEditable) {
              return;
            }
            onChange({ ...action, snoozePeriod: item.value });
          }}
          selectedItem={selectedSnoozePeriod}
        />
      )}
    </Flex>
  );
});
